import { formatAddress, formatEmail, formatPhone } from './formatters';
import { inferAssociationTypeFromText, inferNumberOfUnitsFromRangeText } from './parseLeadDefaults';

const titleCase = s =>
  String(s || '')
    .toLowerCase()
    .split(/\s+/)
    .map(w => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');

const normalizeState = raw => {
  const map = {
    alabama: 'AL',
    alaska: 'AK',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    florida: 'FL',
    georgia: 'GA',
    hawaii: 'HI',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY',
    'district of columbia': 'DC',
  };
  if (!raw) return '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
  const key = s.toLowerCase();
  return map[key] || s;
};

export function parseLead(raw) {
  if (!raw) return {};
  const lines = raw
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);
  const map = {};
  for (const line of lines) {
    const m = line.match(/^([^:]+):\s*(.*)$/);
    if (m) map[m[1].toLowerCase()] = m[2];
    else {
      if (!map['association'] && line.length > 3 && line.split(' ').length <= 6)
        map['association'] = line;
      if (!map['note'] && /note[:-]/i.test(line)) map['note'] = line.replace(/note[:-]\s*/i, '');
    }
  }

  const assocNameRaw =
    map['association name'] || map['association'] || map['property'] || map['property name'];
  const name = assocNameRaw ? titleCase(assocNameRaw.trim()) : undefined;

  const contactName = map['contact name']
    ? titleCase(map['contact name'])
    : map['first name'] || map['last name']
      ? `${map['first name'] || ''} ${map['last name'] || ''}`.trim()
      : undefined;

  const contactEmail = map['email'] ? formatEmail(map['email']) : undefined;
  const phone = map['phone'] ? formatPhone(map['phone']) : undefined;

  const street = map['property street'] || map['address'] || map['street'] || '';
  const city = map['property city'] || map['city'] || '';
  const stateRaw = map['property state'] || map['state'] || '';
  const postal = map['zip'] || map['postal'] || map['postal code'] || map['postalcode'] || map['property postal code'] || '';
  const state = normalizeState(stateRaw);
  const parts = [];
  if (street) parts.push(titleCase(formatAddress(street)));
  if (city) parts.push(titleCase(formatAddress(city)));
  if (state) parts.push(state);
  if (postal) parts.push(String(postal).trim());
  const address = parts.length ? parts.join(', ') : undefined;

  // Parse number of units with intelligent range inference
  const unitsRaw = map['units'] || map['number of units'] || map['units:'] || undefined;
  let numberOfUnits = unitsRaw;

  // If we have a range like "2-49 units", infer the midpoint
  if (unitsRaw && typeof unitsRaw === 'string') {
    const inferredUnits = inferNumberOfUnitsFromRangeText(unitsRaw);
    if (inferredUnits !== null) {
      numberOfUnits = inferredUnits;
    }
  }

  // Parse contact role from association role field
  const contactRole =
    map['association role'] || map['role'] || map['title'] || map['position'] || undefined;

  const notes = map['note'] || map['notes'] || undefined;

  // Use the comprehensive type inference from parseLeadDefaults
  const textForTypeInference = [
    map['association'] || '',
    map['property'] || '',
    map['association name'] || '',
    map['property name'] || '',
    notes || '',
  ].join(' ');

  const type = inferAssociationTypeFromText(textForTypeInference);

  return {
    name,
    contactName,
    contactEmail,
    phone,
    address,
    numberOfUnits,
    contactRole,
    notes,
    type,
  };
}

export default parseLead;
